import { Switch } from '@/components/ui/switch'
import { Typography } from '@/components/ui/typography'
import { CustomerPaymentTerms } from '@/lib/enums/CustomerPaymentTerms'
import { cn } from '@/lib/utils'
import { Building2 } from 'lucide-react'
import { useId } from 'react'

export const HOUSE_ACCOUNT_DEFAULT_CREDIT_LIMIT = '1000'

export function isHouseAccountEnabled(payLaterAllowed, allowFulfillmentBeforePayment) {
  return payLaterAllowed !== false && allowFulfillmentBeforePayment === true
}

export function getPaymentTermsDisabledTooltip(payLaterAllowed, allowFulfillmentBeforePayment) {
  if (!payLaterAllowed) {
    return (
      <>
        Turn on Allow Pay Later Orders and{' '}
        <strong className="font-semibold">Allow Fulfillment Before Payment</strong> to set payment
        terms.
      </>
    )
  }

  if (!allowFulfillmentBeforePayment) {
    return (
      <>
        Turn on <strong className="font-semibold">Allow Fulfillment Before Payment</strong> to set
        payment terms.
      </>
    )
  }

  return null
}

export function setHouseAccountEnabled(
  form,
  enabled,
  {
    payLaterField = 'pay_later_allowed',
    fulfillmentField = 'allow_fulfillment_before_payment',
    creditLimitField = 'credit_limit',
    paymentTermsCodeField = 'payment_terms_code',
    paymentTermsDaysField = 'payment_terms_days',
    paymentTermsDaysDefault = '',
  } = {}
) {
  if (!enabled) {
    form.setFieldValue(fulfillmentField, false)
    return
  }

  const currentCreditLimit = Number.parseFloat(String(form.getFieldValue(creditLimitField) ?? ''))

  form.setFieldValue(payLaterField, true)
  form.setFieldValue(fulfillmentField, true)
  if (!Number.isFinite(currentCreditLimit) || currentCreditLimit <= 0) {
    form.setFieldValue(creditLimitField, HOUSE_ACCOUNT_DEFAULT_CREDIT_LIMIT)
  }
  form.setFieldValue(paymentTermsCodeField, CustomerPaymentTerms.DueOnReceipt.Value)
  form.setFieldValue(paymentTermsDaysField, paymentTermsDaysDefault)
}

export function HouseAccountSettingsSection({
  children,
  className,
  description = 'House Accounts can place orders and receive pickup or delivery before payment is collected.',
  disabled = false,
  enabled,
  onEnabledChange,
}) {
  const switchId = useId()

  return (
    <fieldset
      aria-label="House Account"
      className={cn(
        'min-w-0 rounded-xl border border-border/70 px-4 pb-4 sm:px-5 sm:pb-5',
        className
      )}
    >
      <legend className="ml-1 px-2">
        <div className="inline-flex min-h-11 items-center gap-3">
          <label
            htmlFor={switchId}
            className={cn(
              'inline-flex cursor-pointer items-center gap-2',
              disabled && 'cursor-not-allowed'
            )}
          >
            <Building2 className="size-4 shrink-0 text-muted-foreground" aria-hidden="true" />
            <div className="text-sm font-semibold text-foreground">House Account</div>
          </label>
          <Switch
            id={switchId}
            aria-label="House Account"
            checked={enabled}
            disabled={disabled}
            onCheckedChange={onEnabledChange}
          />
        </div>
      </legend>

      <div className="space-y-5 pt-1">
        <Typography variant="supporting" wrap="pretty" className="block max-w-2xl">
          {description}
        </Typography>
        <div className="space-y-5">{children}</div>
      </div>
    </fieldset>
  )
}
